import React, { FC } from "react"
import Helpers from "../../../../utils/Helpers"
import { useUserDeductible } from "../../../context/user/DeductibleContext"
import { getPiiProps } from "#/services/tracking"
import { useIntl } from "react-intl"
import { Box, Divider, Stack, Typography } from "@carrotfertility/carotene-core"
import type { OutOfPocketMaxSnapshot } from "#/services/reimbursements/types/ReimbursementDetails"

type OOPMRowProps = {
  name: string
  value: string
}

type OOPMHeaderProps = {
  startDate: string
  endDate: string
}

type OOPMTableProps = {
  startDate: string
  endDate: string
  totalAmount: number
  amountApplied: number
  amountRemaining: number
}

const OOPMHeader: FC<OOPMHeaderProps> = ({ startDate, endDate }) => {
  const intl = useIntl()
  const formattedStartDate = intl.formatDate(startDate, { month: "short", day: "numeric", year: "numeric" })
  const formattedEndDate = intl.formatDate(endDate, { month: "short", day: "numeric", year: "numeric" })
  const planYear = intl.formatMessage(
    { defaultMessage: "Plan year: {startDate} - {endDate}" },
    { startDate: formattedStartDate, endDate: formattedEndDate }
  )

  return (
    <Box paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
      <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
        {planYear}
      </Typography>
    </Box>
  )
}

const OOPMRow: FC<OOPMRowProps> = ({ name, value }) => {
  return (
    <Box
      paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      width="100%"
    >
      <Typography color={(theme) => theme.palette.text.secondary} variant="body1">
        {name}
      </Typography>
      <Box {...getPiiProps()}>
        <Typography color={(theme) => theme.palette.text.secondary} variant="body1">
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

const OOPMTable: FC<OOPMTableProps> = ({ startDate, endDate, totalAmount, amountApplied, amountRemaining }) => {
  const intl = useIntl()

  return (
    <Stack>
      <Box
        padding={(theme) => theme.spacing(theme.tokens.spacing.md)}
        display="flex"
        bgcolor={(theme) => theme.palette.background.default}
        borderRadius={(theme) => theme.tokens.borderRadius.sm}
        border={(theme) => `1px solid ${theme.palette.divider}`}
      >
        <Box width="100%">
          <OOPMHeader startDate={startDate} endDate={endDate} />
          <OOPMRow
            name={intl.formatMessage({ defaultMessage: "Out of pocket maximum" })}
            value={intl.formatNumber(totalAmount / 100, {
              style: "currency",
              currency: "USD",
              currencyDisplay: "narrowSymbol",
              maximumFractionDigits: 0
            })}
          />
          <OOPMRow
            name={intl.formatMessage({ defaultMessage: "Amount spent" })}
            value={
              (amountApplied > 0 ? "-" : "") +
              intl.formatNumber(amountApplied / 100, {
                style: "currency",
                currency: "USD",
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 0
              })
            }
          />
          <Box
            paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
            paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}
          >
            <Divider />
          </Box>
          <OOPMRow
            name={intl.formatMessage({ defaultMessage: "Remaining out of pocket maximum" })}
            value={intl.formatNumber(amountRemaining / 100, {
              style: "currency",
              currency: "USD",
              currencyDisplay: "narrowSymbol",
              maximumFractionDigits: 0
            })}
          />
        </Box>
      </Box>
    </Stack>
  )
}

function OOPMTableContainer(): JSX.Element {
  const { outOfPocketMaxSnapshot } = useUserDeductible() ?? {}

  if (!outOfPocketMaxSnapshot) {
    return <></>
  }

  const snapshot: OutOfPocketMaxSnapshot = outOfPocketMaxSnapshot
  const startDate = Helpers.formatDate(snapshot.planStartDate)
  const endDate = Helpers.formatDate(snapshot.planEndDate)

  return (
    <OOPMTable
      startDate={startDate}
      endDate={endDate}
      totalAmount={snapshot.totalAmount}
      amountApplied={snapshot.amountApplied}
      amountRemaining={snapshot.amountRemaining}
    />
  )
}

export { OOPMTableContainer }
